/* ═══════════════════════════════════════════════════════════════════════════════
   Progress Payment Panel Tour (v2.4.0)

   Feature update tour shown when the Progress Payment panel first opens.
   Walks through:
   - Progress claim breakdown table
   - Action dropdown (Paid / Agreement Made / Voided / Works Not Completed)
   - Amount Paid field
   - Batch actions (All Paid / All Void)
   - Reset button
   - Save behaviour (pending items are skipped)
   - Dashpivot sync for variations
   ═══════════════════════════════════════════════════════════════════════════════ */

import type { TourDefinition } from './types';

export const progressPaymentPanelTour: TourDefinition = {
  id: 'progress_payment_panel_v2_4',
  version: '2.4.0',
  type: 'feature_update',
  audience: 'all',
  page: 'progress_payment_panel',
  priority: 1,
  title: 'Progress Payment Panel',
  subtitle: 'Progress claim payment tracking with smart actions.',

  steps: [
    // ── Step 0: Welcome ──────────────────────────────────────────────
    {
      id: 'welcome',
      type: 'welcome',
      title: 'How Progress Payments Work',
      description: 'Each progress claim is broken into subcategories from your contract. You\'ll set an action for each one: Paid, Agreement Made, Voided, or Works Not Completed. Here\'s what each option does.',
      icon: '📋',
      actionLabel: 'Show Me',
    },

    // ── Step 1: Progress Claim Breakdown ─────────────────────────────
    {
      id: 'subcategory-table',
      type: 'spotlight',
      targetSelector: '[data-tour-id="pp-subcategory-table"]',
      position: 'right',
      title: 'Progress Claim Breakdown',
      description: 'Each row represents a claimed category/subcategory amount for this period. Review the payment schedule, enter the amount paid where needed, and set the payment action for each line.',
      icon: '📑',
      actionLabel: 'Next',
      spotlightPadding: 8,
    },

    // ── Step 2: Action Dropdown ──────────────────────────────────────
    {
      id: 'action-dropdown',
      type: 'spotlight',
      targetSelector: '[data-tour-id="pp-action-dropdown"]',
      position: 'left',
      title: 'Payment Actions',
      description: '• Paid: 100% of the claimed amount is paid (auto-filled)\n• Agreement Made: partial payment, you enter the agreed amount\n• Voided: claim is rejected, sets to $0\n• Works Not Completed: pushes the claim to next month\'s report',
      icon: '🎯',
      actionLabel: 'Next',
      spotlightPadding: 8,
    },

    // ── Step 3: Paid Fields ─────────────────────────────────────────
    {
      id: 'paid-fields',
      type: 'spotlight',
      targetSelector: '[data-tour-id="pp-paid-fields"]',
      position: 'left',
      title: 'Amount Paid Field',
      description: 'Enter the amount from the payment schedule. For "Paid", it auto-fills to the claimed amount. For "Voided", "Held", and "Works Not Completed", it stays at $0.',
      icon: '✏️',
      actionLabel: 'Next',
      spotlightPadding: 8,
    },

    // ── Step 4: Batch Actions ────────────────────────────────────────
    {
      id: 'batch-actions',
      type: 'spotlight',
      targetSelector: '[data-tour-id="pp-batch-actions"]',
      position: 'bottom',
      title: 'Quick Batch Actions',
      description: '"All Paid" sets every line to its full claimed amount in one click. "All Void" rejects everything. Use these to save time, then adjust individual amounts as needed.',
      icon: '⚡',
      actionLabel: 'Next',
      spotlightPadding: 8,
    },

    // ── Step 5: Reset Button ────────────────────────────────────────
    {
      id: 'reset-button',
      type: 'spotlight',
      targetSelector: '[data-tour-id="pp-reset-button"]',
      position: 'top',
      title: 'Reset All Data',
      description: 'Reset clears all payment data for this period and closes the panel. The claim goes back to its unpaid state, which is useful if you need to start the reconciliation from scratch.',
      icon: '🔄',
      actionLabel: 'Next',
      spotlightPadding: 8,
    },

    // ── Step 6: Save Button ─────────────────────────────────────────
    {
      id: 'save-button',
      type: 'spotlight',
      targetSelector: '[data-tour-id="pp-save-button"]',
      position: 'top',
      title: 'Smart Save',
      description: 'Save only records items where you\'ve selected an action. Items left on "Select Action" are skipped, so you can partially reconcile a claim and come back later to finish the rest.',
      icon: '💾',
      actionLabel: 'Next',
      spotlightPadding: 10,
    },

    // ── Step 7: Dashpivot Sync Note ─────────────────────────────────
    {
      id: 'dashpivot-sync',
      type: 'spotlight',
      targetSelector: '[data-tour-id="pp-dashpivot-note"]',
      position: 'top',
      title: 'Syncs to Dashpivot',
      description: 'Payment data saved here flows back to Dashpivot reports. For "Works Not Completed", the uncompleted work is automatically pushed to the next month\'s claim report, with no manual re-entry needed.',
      icon: '🔗',
      actionLabel: 'Got It',
      spotlightPadding: 12,
    },

    // ── Step 8: Completion ───────────────────────────────────────────
    {
      id: 'completion',
      type: 'completion',
      title: 'You\'re All Set!',
      description: 'Use the action dropdown for each subcategory, then hit Save. For quick reconciliation, try "All Paid" and adjust from there. The Variations and Retention panels work the same way.',
      icon: '🎉',
      actionLabel: 'Start Reconciling',
    },
  ],
};
